<template>
  <div class="grc-controls-list">
    <b-row>
      <b-col cols="12">
        <b-card>
          <template #header>
            <div
              class="d-flex align-items-center justify-content-between flex-wrap grc-gap-2"
            >
              <div>
                <h5 class="mb-0">Controles</h5>
                <small class="text-muted"
                  >Cadastro e teste de controles vinculados a requisitos e
                  riscos.</small
                >
              </div>
              <div class="d-flex flex-wrap grc-gap-2">
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  :disabled="loading || exporting"
                  @click="exportCsv"
                >
                  {{ exporting ? "Exportando..." : "Exportar CSV" }}
                </b-button>
                <b-button
                  v-if="canCreate"
                  size="sm"
                  variant="primary"
                  @click="openNew"
                >
                  Novo controle
                </b-button>
              </div>
            </div>
          </template>
          <b-card-body>
            <div class="grc-filters-section">
              <b-form-group label="Pesquisar" class="grc-search-filter">
                <b-input-group>
                  <b-input-group-prepend>
                    <b-input-group-text>
                      <i class="fas fa-search" aria-hidden="true"></i>
                    </b-input-group-text>
                  </b-input-group-prepend>
                  <b-form-input
                    v-model="filters.search"
                    type="search"
                    placeholder="Nome, objetivo ou categoria"
                    @keyup.enter="applyFilters"
                  ></b-form-input>
                  <b-input-group-append>
                    <b-button :disabled="!filters.search" @click="clearSearch">
                      Limpar
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
              <b-form-group label="Status">
                <b-form-select
                  v-model="filters.status"
                  :options="statusFilterOptions"
                ></b-form-select>
              </b-form-group>
              <b-form-group label="Área">
                <b-form-select
                  v-model="filters.area"
                  :options="areaFilterOptions"
                ></b-form-select>
              </b-form-group>
              <b-button variant="outline-secondary" @click="applyFilters">
                Filtrar
              </b-button>
            </div>

            <b-alert
              v-if="activeStatusSummary"
              show
              variant="info"
              class="py-2"
            >
              Recorte do dashboard: {{ activeStatusSummary }}
            </b-alert>

            <GRCListTable
              :items="data.docs"
              :fields="fields"
              :busy="loading"
              :page="data.page"
              :total-rows="data.total"
              :per-page="data.limit"
              :sort-by="sortBy"
              :sort-desc="sortDesc"
              server-sorting
              empty-text="Nenhum controle encontrado"
              busy-text="Carregando controles..."
              @sort-changed="onSortChanged"
              @page-change="changePage"
            >
              <template #cell(name)="slotData">
                <div>
                  <strong>{{ slotData.item.name }}</strong>
                  <div class="text-muted small">
                    {{ slotData.item.objective }}
                  </div>
                </div>
              </template>
              <template #cell(area)="slotData">
                {{ slotData.item.area ? slotData.item.area.name : "-" }}
              </template>
              <template #cell(controlType)="slotData">
                {{ controlTypeLabel(slotData.item.controlType) }}
              </template>
              <template #cell(status)="slotData">
                <b-badge :variant="statusVariant(slotData.item.status)">
                  {{ statusLabel(slotData.item.status) }}
                </b-badge>
              </template>
              <template #cell(effectiveness)="slotData">
                {{
                  slotData.item.effectiveness === null ||
                  slotData.item.effectiveness === undefined
                    ? "-"
                    : `${slotData.item.effectiveness}%`
                }}
              </template>
              <template #cell(nextTestAt)="slotData">
                {{ formatDate(slotData.item.nextTestAt) }}
              </template>
              <template #cell(actions)="slotData">
                <div class="grc-action-buttons">
                  <b-button
                    v-if="canEdit"
                    size="sm"
                    variant="link"
                    class="grc-icon-button grc-icon-button--edit"
                    title="Editar"
                    aria-label="Editar"
                    @click="openEdit(slotData.item)"
                  >
                    <i class="fa fa-edit" aria-hidden="true"></i>
                  </b-button>
                  <b-button
                    v-if="
                      canApprove &&
                      getApprovalStatus(slotData.item) === 'pending'
                    "
                    size="sm"
                    variant="outline-success"
                    @click="approve(slotData.item)"
                  >
                    Aprovar
                  </b-button>
                  <b-button
                    v-if="
                      canApprove &&
                      getApprovalStatus(slotData.item) === 'pending'
                    "
                    size="sm"
                    variant="outline-warning"
                    @click="reject(slotData.item)"
                  >
                    Rejeitar
                  </b-button>
                  <b-button
                    v-if="canDelete"
                    size="sm"
                    variant="link"
                    class="grc-icon-button grc-icon-button--delete"
                    title="Inativar"
                    aria-label="Inativar"
                    @click="remove(slotData.item)"
                  >
                    <i class="fa fa-trash" aria-hidden="true"></i>
                  </b-button>
                </div>
              </template>
              <template #empty>
                <div class="text-muted text-center py-5">
                  <i class="fas fa-sliders-h fa-2x"></i>
                  <p class="mt-3 mb-0">Nenhum controle encontrado</p>
                </div>
              </template>
            </GRCListTable>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <GRCFormSidebar
      id="grc-control-sidebar"
      :visible.sync="showSidebar"
      :title="selectedControl ? 'Editar controle' : 'Novo controle'"
      @hidden="onModalHidden"
    >
      <GRCControlForm
        :control-record="selectedControl"
        :area-options="areaOptions"
        :scope-options="scopeOptions"
        :project-options="projectOptions"
        :requirement-options="requirementOptions"
        :risk-options="riskOptions"
        :user-options="userOptions"
        :saving="saving"
        @submit="saveControl"
        @cancel="showSidebar = false"
      />
    </GRCFormSidebar>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { getUsers } from "@/api/user";
import { getGrcMetadata } from "@/api/grc-metadata";
import { exportRowsToCsv } from "@/utils/csv-export";
import { getGrcRequirements } from "@/api/grc-requirements";
import { getGrcRisks } from "@/api/grc-risks";
import {
  approveGrcControl,
  createGrcControl,
  deleteGrcControl,
  getGrcControl,
  getGrcControls,
  rejectGrcControl,
  updateGrcControl,
} from "@/api/grc-controls";
import GRCFormSidebar from "./components/GRCFormSidebar.vue";
import GRCListTable from "./components/GRCListTable.vue";
import GRCControlForm from "./GRCControlForm.vue";

const STATUS = {
  NOT_CONTROLLED: 1,
  PARTIALLY_CONTROLLED: 2,
  CONTROLLED: 3,
  IN_REVIEW: 4,
};

export default {
  name: "GRCControlsList",
  components: {
    GRCFormSidebar,
    GRCListTable,
    GRCControlForm,
  },
  data() {
    return {
      loading: false,
      exporting: false,
      saving: false,
      sortBy: "nextTestAt",
      sortDesc: false,
      showSidebar: false,
      selectedControl: null,
      fields: [
        {
          key: "_id",
          label: "#",
          sortable: true,
          thStyle: { width: "90px !important" },
        },
        {
          key: "name",
          label: "Controle",
          sortable: true,
          thStyle: { "min-width": "240px !important" },
        },
        {
          key: "area",
          label: "Área",
          thStyle: { "min-width": "170px !important" },
        },
        {
          key: "controlType",
          label: "Tipo",
          sortable: true,
          thStyle: { width: "170px !important" },
        },
        {
          key: "status",
          label: "Status",
          sortable: true,
          thStyle: { width: "170px !important" },
        },
        {
          key: "effectiveness",
          label: "Efetividade",
          sortable: true,
          thStyle: { width: "140px !important" },
        },
        {
          key: "nextTestAt",
          label: "Próximo teste",
          sortable: true,
          thStyle: { width: "170px !important" },
        },
        {
          key: "actions",
          label: "Ações",
          thStyle: { width: "220px !important" },
        },
      ],
      filters: {
        search: "",
        status: null,
        area: null,
      },
      data: {
        docs: [],
        total: 0,
        page: 1,
        pages: 0,
        limit: 10,
      },
      areaOptions: [],
      scopeOptions: [],
      projectOptions: [],
      userOptions: [],
      requirementOptions: [],
      riskOptions: [],
      routeStatusValues: [],
    };
  },
  computed: {
    ...mapGetters(["profile", "permissions"]),
    currentCompanyId() {
      const companyId = parseInt(this.$route.query.company, 10);
      return Number.isNaN(companyId) ? null : companyId;
    },
    statusFilterOptions() {
      return [
        { value: null, text: "Todos" },
        { value: STATUS.NOT_CONTROLLED, text: "Não controlado" },
        {
          value: STATUS.PARTIALLY_CONTROLLED,
          text: "Parcialmente controlado",
        },
        { value: STATUS.CONTROLLED, text: "Controlado" },
        { value: STATUS.IN_REVIEW, text: "Em revisão" },
      ];
    },
    areaFilterOptions() {
      return [{ value: null, text: "Todas" }, ...this.areaOptions];
    },
    canCreate() {
      return this.hasControlPermission("create");
    },
    canEdit() {
      return this.hasControlPermission("edit");
    },
    canDelete() {
      return this.hasControlPermission("delete");
    },
    canApprove() {
      return this.hasControlPermission("approve");
    },
    activeStatusSummary() {
      if (this.routeStatusValues.length <= 1) {
        return "";
      }

      return this.routeStatusValues
        .map((value) => this.statusLabel(value))
        .join(", ");
    },
  },
  watch: {
    "$route.query": {
      async handler(newQuery, oldQuery) {
        const nextCompanyId = this.normalizeQueryNumber(newQuery.company);
        const previousCompanyId = this.normalizeQueryNumber(
          oldQuery && oldQuery.company
        );

        this.applyRouteState();

        if (nextCompanyId !== previousCompanyId) {
          await this.loadSupportData();
        }

        await this.loadControls();
      },
    },
  },
  async created() {
    this.applyRouteState();
    await this.loadSupportData();
    await this.loadControls();
  },
  methods: {
    normalizeQueryNumber(value) {
      const parsed = parseInt(value, 10);
      return Number.isNaN(parsed) ? null : parsed;
    },
    parseQueryNumberList(value) {
      if (value === null || value === undefined || value === "") {
        return [];
      }

      const rawValues = Array.isArray(value) ? value : [value];
      return Array.from(
        new Set(
          rawValues
            .flatMap((entry) => String(entry).split(","))
            .map((entry) => this.normalizeQueryNumber(entry))
            .filter(Boolean)
        )
      );
    },
    getRequestParams(extra = {}) {
      return {
        ...extra,
        company: this.currentCompanyId || undefined,
      };
    },
    getListParams(extra = {}) {
      return this.getRequestParams({
        page: this.data.page,
        size: this.data.limit,
        sortBy: this.sortBy || undefined,
        sortDesc: this.sortDesc,
        search: this.filters.search || undefined,
        status:
          this.routeStatusValues.length <= 1
            ? this.filters.status || undefined
            : undefined,
        statuses:
          this.routeStatusValues.length > 1
            ? this.routeStatusValues.join(",")
            : undefined,
        area: this.filters.area || undefined,
        ...extra,
      });
    },
    getMutationPayload(payload) {
      if (!this.currentCompanyId) {
        return payload;
      }

      return {
        ...payload,
        company: this.currentCompanyId,
      };
    },
    applyRouteState() {
      const query = this.$route.query || {};
      const statuses = this.parseQueryNumberList(
        query.statuses || query.status
      );

      this.filters.search =
        typeof query.search === "string" ? query.search : "";
      this.routeStatusValues = statuses;
      this.filters.status = statuses.length === 1 ? statuses[0] : null;
      this.filters.area = this.normalizeQueryNumber(query.area);
      this.data.page = this.normalizeQueryNumber(query.page) || 1;
    },
    buildRouteQuery(overrides = {}) {
      const search = Object.prototype.hasOwnProperty.call(overrides, "search")
        ? overrides.search
        : this.filters.search;
      const status = Object.prototype.hasOwnProperty.call(overrides, "status")
        ? overrides.status
        : this.filters.status;
      const statuses = Object.prototype.hasOwnProperty.call(
        overrides,
        "statuses"
      )
        ? this.parseQueryNumberList(overrides.statuses)
        : status
        ? []
        : this.routeStatusValues;
      const area = Object.prototype.hasOwnProperty.call(overrides, "area")
        ? overrides.area
        : this.filters.area;
      const page = Object.prototype.hasOwnProperty.call(overrides, "page")
        ? overrides.page
        : this.data.page;
      const company = Object.prototype.hasOwnProperty.call(overrides, "company")
        ? overrides.company
        : this.currentCompanyId;
      const query = {};

      if (company) {
        query.company = String(company);
      }
      if (search) {
        query.search = search;
      }
      if (status) {
        query.status = String(status);
      } else if (statuses.length > 1) {
        query.statuses = statuses.join(",");
      }
      if (area) {
        query.area = String(area);
      }
      if (page && page > 1) {
        query.page = String(page);
      }

      return query;
    },
    hasSameRouteQuery(query) {
      return ["company", "search", "status", "statuses", "area", "page"].every(
        (key) => {
          const currentValue =
            this.$route.query[key] === undefined
              ? null
              : String(this.$route.query[key]);
          const nextValue =
            query[key] === undefined ? null : String(query[key]);
          return currentValue === nextValue;
        }
      );
    },
    syncRouteQuery(overrides = {}) {
      const query = this.buildRouteQuery(overrides);

      if (this.hasSameRouteQuery(query)) {
        this.applyRouteState();
        return this.loadControls();
      }

      return this.$router.replace({
        path: this.$route.path,
        query,
      });
    },
    mapUserOptions(users) {
      const scopedUsers = this.currentCompanyId
        ? users.filter((item) => {
            const companyId = this.normalizeQueryNumber(
              item.company?._id || item.company
            );
            return companyId === this.currentCompanyId;
          })
        : users;

      return scopedUsers.map((item) => ({
        value: item._id,
        text: item.fullname || `${item.name} ${item.surname || ""}`.trim(),
      }));
    },
    hasControlPermission(permission) {
      const role = this.profile.role;
      if (role === "company-root" || role === "root") {
        return true;
      }

      if (parseInt(role) === role) {
        return Boolean(this.permissions?.controls?.[permission]);
      }

      return false;
    },
    async loadSupportData() {
      try {
        const [
          areasResponse,
          scopesResponse,
          projectsResponse,
          usersResponse,
          requirementsResponse,
          risksResponse,
        ] = await Promise.all([
          getGrcMetadata("areas", this.getRequestParams()),
          getGrcMetadata("scopes", this.getRequestParams()),
          getGrcMetadata("projects", this.getRequestParams()),
          getUsers({ page: 1, size: 200 }),
          getGrcRequirements(this.getRequestParams({ page: 1, size: 200 })),
          getGrcRisks(this.getRequestParams({ page: 1, size: 200 })),
        ]);

        this.areaOptions = areasResponse.data.map((item) => ({
          value: item._id,
          text: item.code ? `${item.code} - ${item.name}` : item.name,
          selectedText: item.name,
        }));
        this.scopeOptions = scopesResponse.data.map((item) => ({
          value: item._id,
          text: item.area?.name
            ? `${item.name} (${item.area.name})`
            : item.name,
          selectedText: item.name,
        }));
        this.projectOptions = projectsResponse.data.map((item) => ({
          value: item._id,
          text: item.code ? `${item.code} - ${item.name}` : item.name,
          selectedText: item.name,
        }));
        this.userOptions = this.mapUserOptions(usersResponse.data.docs || []);
        this.requirementOptions = (requirementsResponse.data.docs || []).map(
          (item) => ({
            value: item._id,
            text: item.title,
            selectedText: item.title,
          })
        );
        this.riskOptions = (risksResponse.data.docs || []).map((item) => ({
          value: item._id,
          text: `${item.title} (${item.score})`,
          selectedText: item.title,
        }));
      } catch (error) {
        this.showError(error, "Não foi possível carregar os dados auxiliares");
      }
    },
    async loadControls() {
      this.loading = true;
      try {
        const response = await getGrcControls(this.getListParams());
        this.data = response.data;
      } catch (error) {
        this.showError(error, "Não foi possível carregar os controles");
      } finally {
        this.loading = false;
      }
    },
    async exportCsv() {
      this.exporting = true;
      try {
        const response = await getGrcControls(
          this.getListParams({
            page: 1,
            size: Math.max(this.data.total || this.data.limit || 1, 1),
          })
        );
        const rows = (response.data.docs || []).map((item) => [
          item._id,
          item.name,
          item.area ? item.area.name : "-",
          this.controlTypeLabel(item.controlType),
          this.statusLabel(item.status),
          item.effectiveness === null || item.effectiveness === undefined
            ? "-"
            : `${item.effectiveness}%`,
          this.formatDate(item.nextTestAt),
        ]);

        exportRowsToCsv(
          `grc_controles_${new Date()
            .toISOString()
            .slice(0, 19)
            .replace(/[:T]/g, "-")}.csv`,
          [
            "ID",
            "Controle",
            "Área",
            "Tipo",
            "Status",
            "Efetividade",
            "Próximo teste",
          ],
          rows
        );
      } catch (error) {
        this.showError(error, "Não foi possível exportar os controles");
      } finally {
        this.exporting = false;
      }
    },
    applyFilters() {
      this.data.page = 1;
      this.syncRouteQuery({ page: 1 });
    },
    clearSearch() {
      this.filters.search = "";
      this.applyFilters();
    },
    changePage(page) {
      this.data.page = page;
      this.syncRouteQuery({ page });
    },
    onSortChanged({ sortBy, sortDesc }) {
      this.sortBy = sortBy;
      this.sortDesc = sortDesc;
      this.data.page = 1;
      if (this.$route.query.page) {
        this.syncRouteQuery({ page: 1 });
        return;
      }
      this.loadControls();
    },
    openNew() {
      this.selectedControl = null;
      this.showSidebar = true;
    },
    async openEdit(item) {
      try {
        const response = await getGrcControl(item._id, this.getRequestParams());
        this.selectedControl = response.data;
        this.showSidebar = true;
      } catch (error) {
        this.showError(error, "Não foi possível carregar o controle");
      }
    },
    onModalHidden() {
      this.selectedControl = null;
    },
    async saveControl(payload) {
      this.saving = true;
      try {
        const requestParams = this.getRequestParams();
        const data = this.getMutationPayload(payload);
        if (this.selectedControl && this.selectedControl._id) {
          await updateGrcControl(this.selectedControl._id, data, requestParams);
        } else {
          await createGrcControl(data, requestParams);
        }

        this.$bvToast.toast("Controle salvo com sucesso.", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
        });
        this.showSidebar = false;
        await this.loadControls();
      } catch (error) {
        this.showError(error, "Não foi possível salvar o controle");
      } finally {
        this.saving = false;
      }
    },
    async approve(item) {
      try {
        await approveGrcControl(item._id, {}, this.getRequestParams());
        this.$bvToast.toast("Controle aprovado com sucesso.", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
        });
        await this.loadControls();
      } catch (error) {
        this.showError(error, "Não foi possível aprovar o controle");
      }
    },
    async reject(item) {
      const reason = window.prompt("Informe o motivo da rejeição:");
      if (!reason) {
        return;
      }

      try {
        await rejectGrcControl(item._id, { reason }, this.getRequestParams());
        this.$bvToast.toast("Controle rejeitado com sucesso.", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
        });
        await this.loadControls();
      } catch (error) {
        this.showError(error, "Não foi possível rejeitar o controle");
      }
    },
    async remove(item) {
      const confirmed = window.confirm(
        `Deseja inativar o controle ${item.name}?`
      );
      if (!confirmed) {
        return;
      }

      try {
        await deleteGrcControl(item._id, this.getRequestParams());
        this.$bvToast.toast("Controle inativado com sucesso.", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
        });
        await this.loadControls();
      } catch (error) {
        this.showError(error, "Não foi possível inativar o controle");
      }
    },
    statusLabel(value) {
      const labels = {
        [STATUS.NOT_CONTROLLED]: "Não controlado",
        [STATUS.PARTIALLY_CONTROLLED]: "Parcialmente controlado",
        [STATUS.CONTROLLED]: "Controlado",
        [STATUS.IN_REVIEW]: "Em revisão",
      };
      return labels[value] || "-";
    },
    statusVariant(value) {
      const variants = {
        [STATUS.NOT_CONTROLLED]: "danger",
        [STATUS.PARTIALLY_CONTROLLED]: "warning",
        [STATUS.CONTROLLED]: "success",
        [STATUS.IN_REVIEW]: "info",
      };
      return variants[value] || "secondary";
    },
    controlTypeLabel(value) {
      const labels = {
        preventive: "Preventivo",
        detective: "Detectivo",
        corrective: "Corretivo",
      };
      return labels[value] || "-";
    },
    formatDate(value) {
      if (!value) {
        return "-";
      }
      return new Date(value).toLocaleDateString("pt-BR");
    },
    getApprovalStatus(item) {
      return item && item.approval ? item.approval.status : null;
    },
    showError(error, fallback) {
      const message =
        error?.response?.data || error?.response?.statusText || fallback;
      this.$bvToast.toast(message, {
        title: "Erro",
        toastClass: "custom-toast custom-toast-error",
        variant: "light",
        solid: true,
        autoHideDelay: 10000,
      });
    },
  },
};
</script>

<style scoped>
@import "./grc-list.scss";
</style>
